import { type FC, useCallback, useState } from 'react';
import { addSportFavoritesV2Request, getGroupedLinesContentAnalyticsEvents, getIsLineFinished, removeSportFavoritesV2Request, selectSportFavoritesSetV2, selectUserIsAuthenticated } from '@mwl/core-lib';
import ArrowUpIcon from '@public/assets/common/icons/arrowUp.svg';
import ArrowIcon from '@public/assets/common/icons/arrowUpDouble.svg';
import { ButtonFavorites } from '@/components/ButtonFavorites/ButtonFavorites';
import { ButtonIcon } from '@/components/ButtonIcon/ButtonIcon';
import type { LineOutrightWithEntities } from '@/components/GroupedLines/GroupedLines.types';
import { LineRow } from '@/components/LineRow/LineRow';
import { LineRowCyber } from '@/components/LineRowCyber/LineRowCyber';
import { LineRowOutright } from '@/components/LineRowOutright/LineRowOutright';
import { SportIcon } from '@/components/SportIcon/SportIcon';
import { SportLink } from '@/components/SportLink/SportLink';
import { routes } from '@/constants';
import { useIsCyberSportPage, useIsSportFavoritePage, useSportLinks, useTypedDispatch, useTypedSelector } from '@/hooks';
import type { LineWithEntities } from '@/types';
import type { GroupedLinesContentProps } from './GroupedLinesContent.types';
import styles from './GroupedLinesContent.module.scss';
export const GroupedLinesContent: FC<GroupedLinesContentProps> = ({
  className,
  championshipLines,
  isInSearch,
  variant = 'TRADITIONAL'
}) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const favorites = useTypedSelector(selectSportFavoritesSetV2);
  const dispatch = useTypedDispatch();
  const isFavoritePage = useIsSportFavoritePage();
  const isCyberSportPage = useIsCyberSportPage();
  const isFavorite = favorites.championshipIds.has(championshipLines[0].championshipId);
  const isCyberLine = variant === 'CYBER';
  const isOutrightLine = variant === 'OUTRIGHT';
  const HeaderIcon = isOutrightLine ? ArrowUpIcon : ArrowIcon;
  const activeLines = championshipLines.filter(line => {
    return !('stage' in line && getIsLineFinished(line));
  });
  const closedPregames = championshipLines.filter(line => 'stage' in line && line.stage === 'PREGAME' && line.status === 'CLOSED');
  const analyticsData = getGroupedLinesContentAnalyticsEvents({
    competitors: 'competitors' in championshipLines[0] ? championshipLines[0]?.competitors : [],
    sportCategoryTitle: championshipLines[0].sport.title,
    categoryTitle: championshipLines[0].category.title,
    championshipTitle: championshipLines[0].championship.title,
    matchId: championshipLines[0].id,
    activeLinesCount: activeLines.length,
    isOpen: expanded,
    isFavorite,
    isInSearch,
    isFavoritePage,
    isOutrightLine
  });
  const {
    championshipLink,
    sportIcon
  } = useSportLinks({
    data: championshipLines[0],
    styles,
    analytics: analyticsData,
    options: {
      isInSearch,
      pathname: isCyberSportPage ? routes.esports.home : routes.sport.home,
      isOutrightLine,
      isCyberSportPage: isCyberLine
    }
  });
  const handleToggleFavorites = useCallback(async (id: number, newValue: boolean) => {
    if (newValue) {
      dispatch(addSportFavoritesV2Request({
        championshipIds: [id]
      }));
    } else {
      dispatch(removeSportFavoritesV2Request({
        championshipIds: [id]
      }));
    }
  }, [dispatch]);
  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);
  if (!activeLines.length) {
    return null;
  }
  return <section className={cn(className, styles.root, styles[variant.toLowerCase()], {
    [styles.hidden]: closedPregames.length === championshipLines.length,
    [styles.inSearchRoot]: isInSearch
  })} data-sentry-component="GroupedLinesContent" data-sentry-source-file="GroupedLinesContent.tsx">
      <header className={cn(styles.header, {
      [styles.inSearch]: isInSearch,
      [styles.cyberHeader]: isCyberLine,
      [styles.closedCyberHeader]: isCyberLine && !expanded
    })}>
        <div className={cn(styles.headerContent, {
        [styles.withFavorites]: isAuth,
        [styles.inSearch]: isInSearch,
        [styles.outrightContent]: isOutrightLine
      })}>
          {!isOutrightLine && <SportLink sport={championshipLines[0].sport} className={styles.sportLink}>
              <SportIcon className={styles.icon} size={20} url={sportIcon} />
            </SportLink>}
          <h4 className={cn(styles.title)}>{championshipLink}</h4>

          {!isInSearch && !isOutrightLine && !isCyberLine && <span className={styles.headerLinesCount}>{activeLines.length - closedPregames.length}</span>}
          {isAuth && !isOutrightLine && <ButtonFavorites size="xs" color="red" className={styles.favoriteButton} onClick={inFavorites => handleToggleFavorites(championshipLines[0].championshipId, inFavorites)} value={isFavorite} analytics={isInSearch ? analyticsData.favoritesInSearch : analyticsData.favorites} variant="outlined" />}
        </div>
        {(!isInSearch || isCyberLine) && <ButtonIcon as="button" size="xs" onClick={toggleExpanded} className={styles.arrowButton} aria-label="line toggle" analytics={analyticsData?.dropdown}>
            <HeaderIcon className={cn(styles.arrow, {
          [styles.active]: expanded,
          [styles.outrightHeaderIcon]: isOutrightLine
        })} />
          </ButtonIcon>}
      </header>
      {isCyberLine && expanded && <div className={styles.divider} />}
      <div className={cn(styles.list, {
      [styles.visible]: expanded
    })}>
        {activeLines.map(line => {
        if ('topMarkets' in line) {
          if (isCyberLine) {
            return <LineRowCyber key={line.id} line={(line as LineWithEntities)} className={cn({
              [styles.hidden]: 'stage' in line && line.stage === 'PREGAME' && line.status === 'CLOSED'
            })} isFavorite={favorites.lineIds.has(line.id)} analytics={analyticsData} isInSearch={isInSearch} />;
          }
          return <LineRow key={line.id} line={line} className={cn({
            [styles.hidden]: 'stage' in line && line.stage === 'PREGAME' && line.status === 'CLOSED'
          })} isFavorite={favorites.lineIds.has(line.id)} analytics={analyticsData} isInSearch={isInSearch} />;
        }
        if (isOutrightLine) {
          return <LineRowOutright key={line.id} line={(line as LineOutrightWithEntities)} className={cn({
            [styles.hidden]: 'stage' in line && line.stage === 'PREGAME' && line.status === 'CLOSED'
          })} />;
        }
        return null;
      })}
      </div>
    </section>;
};